import { useEffect, useRef, useState } from 'react';
import ScanbotSDK from 'scanbot-web-sdk';
import type { ITextPatternScannerHandle, TextPatternScannerViewConfiguration } from 'scanbot-web-sdk/@types';

import { useScanbotSDKProvider } from '../scanbotSDKProvider';
import { ScanbotDatacaptureProps } from './scanbot-datacapture.component';

interface ViewProps {
    divRef: React.RefObject<HTMLDivElement | null>;
    detectedWords: string[];
    sdkLoading: boolean;
}

const SCANNER_ID = 'scanbot-datacapture-video';

const useScanbotDatacapture = (props: ScanbotDatacaptureProps): ViewProps => {
    const { scanbotSDK, validLicense, sdkLoading } = useScanbotSDKProvider();
    const [scanner, setScanner] = useState<ITextPatternScannerHandle | null>(null);
    const [detectedWords, setDetectedWords] = useState<string[]>([]);
    const divRef = useRef<HTMLDivElement | null>(null);

    // Store latest callbacks in refs to avoid stale closures without recreating scanner
    const wordsCallbackRef = useRef(props.wordsCallback);
    const rawTextCallbackRef = useRef(props.rawTextCallback);

    // Update refs when callbacks change
    useEffect(() => {
        wordsCallbackRef.current = props.wordsCallback;
        rawTextCallbackRef.current = props.rawTextCallback;
    }, [props.wordsCallback, props.rawTextCallback]);

    useEffect(() => {
        if (scanbotSDK && divRef.current && validLicense) {
            const validator = props.pattern
                ? new ScanbotSDK.Config.PatternContentValidator({
                      pattern: props.pattern,
                      matchSubstring: true
                  })
                : undefined;
            const guidance = props.guidanceText ? { title: { text: props.guidanceText } } : {};
            const configuration: TextPatternScannerViewConfiguration = {
                containerId: SCANNER_ID,
                onTextDetected: (result) => {
                    // Update detected words for visual feedback
                    const words = result.wordBoxes.map((box) => box.text);
                    setDetectedWords((prev) => {
                        if (prev.length === words.length && prev.every((w, i) => w === words[i])) {
                            return prev; // no change → no re-render
                        }
                        return words;
                    });

                    if (validator) {
                        if (result.validationSuccessful) {
                            wordsCallbackRef.current?.(result.wordBoxes.map((box) => box.text));
                            rawTextCallbackRef.current?.(result.rawText);
                        }
                    } else {
                        wordsCallbackRef.current?.(result.wordBoxes.map((box) => box.text));
                        rawTextCallbackRef.current?.(result.rawText);
                    }
                },
                scannerConfiguration: { validator },
                finder: {
                    _type: 'ViewFinderConfiguration',
                    visible: true,
                    aspectRatio: {
                        width: divRef.current.offsetWidth,
                        height: divRef.current.offsetHeight
                    }
                },
                userGuidance: {
                    visible: props.showGuidanceText === true,
                    ...guidance
                }
            };
            scanbotSDK.createTextPatternScanner(configuration).then(setScanner);
        }

        return () => {
            closeScanner(scanner);
        };
    }, [scanbotSDK, divRef, validLicense, props.pattern, props.showGuidanceText, props.guidanceText]);

    useEffect(() => {
        return () => {
            closeScanner(scanner);
        };
    }, [scanner]);

    const closeScanner = (scanner: ITextPatternScannerHandle | null) => {
        if (scanner == null) return;
        const activeCameraInfo = scanner.getActiveCameraInfo();
        if (activeCameraInfo) {
            navigator.mediaDevices.getUserMedia({ video: { deviceId: activeCameraInfo.deviceId } }).then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
            });
        }
        scanner.dispose();
        setScanner(null);
    };

    return {
        divRef,
        detectedWords,
        sdkLoading
    };
};

export default useScanbotDatacapture;
